'use client';

import { useCallback, useState, type KeyboardEvent } from 'react';

export type FilterOptionsMode = 'client' | 'none';

export interface UseComboboxOptions<T> {
  options: T[];
  filterText: string;
  getOptionLabel: (option: T) => string;
  /** When 'client', filter options by filterText; when 'none', return options as-is (e.g. API already filtered). */
  filterOptions?: FilterOptionsMode;
  disabled?: boolean;
  listboxId?: string;
}

export interface UseComboboxReturn {
  open: boolean;
  setOpen: (open: boolean) => void;
  handleOpen: () => void;
  handleClose: () => void;
  /** For trigger input: Enter/Space open menu; ArrowDown/ArrowUp when open move focus into listbox. */
  handleTriggerKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Shared combobox state and behavior: open/close, filtered options, trigger keyboard.
 * Used by Autocomplete (and can be adopted by Select later).
 */
export function useCombobox<T>({
  options,
  filterText,
  getOptionLabel,
  filterOptions = 'client',
  disabled = false,
  listboxId,
}: UseComboboxOptions<T>): UseComboboxReturn & { filteredOptions: T[] } {
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setOpen(true);
  }, [disabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const filteredOptions =
    filterOptions === 'client' && filterText.trim().length > 0
      ? options.filter((opt) =>
          getOptionLabel(opt).toLowerCase().includes(filterText.trim().toLowerCase())
        )
      : options;

  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        handleClose();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleOpen();
        return;
      }
      if (e.key === ' ' && !open) {
        e.preventDefault();
        handleOpen();
        return;
      }
      if (open && listboxId) {
        if (e.key === 'Home') {
          e.preventDefault();
          const listbox = document.getElementById(listboxId);
          const firstOption = listbox?.querySelector<HTMLElement>('[role="option"]');
          firstOption?.focus();
          return;
        }
        if (e.key === 'End') {
          e.preventDefault();
          const listbox = document.getElementById(listboxId);
          const options = listbox?.querySelectorAll<HTMLElement>('[role="option"]');
          options?.[options.length - 1]?.focus();
          return;
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Tab') {
          e.preventDefault();
          const listbox = document.getElementById(listboxId);
          const optionEls = listbox?.querySelectorAll<HTMLElement>('[role="option"]');
          if (optionEls && optionEls.length > 0) {
            const target = e.key === 'ArrowUp' ? optionEls[optionEls.length - 1] : optionEls[0];
            target.focus();
          } else {
            listbox?.focus();
          }
        }
      }
    },
    [handleOpen, handleClose, open, listboxId]
  );

  return {
    open,
    setOpen,
    handleOpen,
    handleClose,
    handleTriggerKeyDown,
    filteredOptions,
  };
}
